import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { Empty } from 'antd';
import { isEqual } from 'lodash';

import { selectors } from 'state';

type XY = { x: string; y: number | null };

const pct = (v: number) => `${(v * 100).toFixed(1)}%`;

const MinimumReserveRequiredGraph: React.FC = () => {
  const players = useSelector(selectors.activePlayers, isEqual);
  const { starting_cash_amount } = useSelector(selectors.activeSession);

  // Build a stable categories list from all players (union), sorted by block_index.
  const categories = useMemo(() => {
    const blocks = new Set<number>();
    for (const p of players ?? []) {
      for (const hs of p.mrr_history ?? []) blocks.add(hs.block_index);
    }
    return [...blocks]
      .sort((a, b) => a - b)
      .map(blockIndex => `${blockIndex * 4 - 3}-${blockIndex * 4}`);
  }, [players]);

  const playerSeries = useMemo(() => {
    return (players ?? []).map(player => {
      const byLabel = new Map<string, number>();
      for (const hs of player.mrr_history ?? []) {
        const label = `${hs.block_index * 4 - 3}-${hs.block_index * 4}`;
        const v = hs.four_period_average_cash / starting_cash_amount;
        if (Number.isFinite(v)) byLabel.set(label, v);
      }

      // Align to categories (important so lines share the same x positions)
      const data: (number | null)[] = categories.map(label => {
        const v = byLabel.get(label);
        return typeof v === 'number' && Number.isFinite(v) ? v : null;
      });

      return {
        name: player.name,
        type: 'line' as const,
        data,
      };
    });
  }, [players, starting_cash_amount, categories]);

  // Benchmark series from "first available" MRR per block across players (same logic as your Highcharts version)
  const benchmarkSeries = useMemo(() => {
    const mrrByBlock = new Map<number, number>();

    for (const p of players ?? []) {
      for (const hs of p.mrr_history ?? []) {
        if (
          !mrrByBlock.has(hs.block_index) &&
          hs.four_period_average_mrr != null
        ) {
          mrrByBlock.set(hs.block_index, hs.four_period_average_mrr);
        }
      }
    }

    const byLabel = new Map<string, number>();
    for (const [blockIndex, mrr] of [...mrrByBlock.entries()].sort(
      ([a], [b]) => a - b
    )) {
      const label = `${blockIndex * 4 - 3}-${blockIndex * 4}`;
      if (typeof mrr === 'number' && Number.isFinite(mrr))
        byLabel.set(label, mrr);
    }

    const data: (number | null)[] = categories.map(label => {
      const v = byLabel.get(label);
      return typeof v === 'number' && Number.isFinite(v) ? v : null;
    });

    return {
      name: 'Required MRR',
      type: 'line' as const,
      data,
      // we’ll dash + disable markers via options (Apex does it per-series by index)
    };
  }, [players, categories]);

  const series = useMemo(() => {
    if (!playerSeries.length) return [];
    return [...playerSeries, benchmarkSeries];
  }, [playerSeries, benchmarkSeries]);

  const options = useMemo((): ApexOptions => {
    const benchmarkIndex = Math.max(0, series.length - 1);

    return {
      chart: {
        type: 'line',
        height: '56.25%',
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      title: {
        text: 'MRR',
      },
      legend: {
        show: true,
        position: 'right',
        horizontalAlign: 'center',
      },
      xaxis: {
        type: 'category',
        categories,
        title: { text: 'Periods' },
      },
      yaxis: {
        labels: {
          formatter: (v: number) => (Number.isFinite(v) ? pct(v) : String(v)),
        },
      },
      stroke: {
        curve: 'straight',
        width: series.map((_, idx) => (idx === benchmarkIndex ? 3 : 2)),
        dashArray: series.map((_, idx) => (idx === benchmarkIndex ? 6 : 0)), // ShortDash-like
      },
      markers: {
        // disable markers only for benchmark (like marker.enabled=false)
        size: series.map((_, idx) => (idx === benchmarkIndex ? 0 : 2)),
      },
      tooltip: {
        shared: false,
        intersect: true,
        y: {
          formatter: (y?: number) => {
            const v = Number(y);
            return Number.isFinite(v) ? pct(v) : '-';
          },
          title: {
            formatter: (seriesName: string) => `${seriesName}: `,
          },
        },
      },
      // Keeps the benchmark visually on top (Apex draws later series on top)
      // benchmarkSeries is appended last in `series`
      grid: {
        padding: { left: 8, right: 8 },
      },
    };
  }, [categories, series.length]);

  if (!playerSeries.length)
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;

  return (
    <Chart
      options={options}
      series={series as any}
      type="line"
      height="56.25%"
    />
  );
};

export default MinimumReserveRequiredGraph;
